import { useQuery } from "@tanstack/react-query";

export interface OrderableMenuPrice {
  price: number;
  service: string;
}

export interface OrderableMenuQuestion {
  id: string;
  name: string;
  text: string;
  format: string;
  order: number;
  required: boolean;
  sub?: boolean;
  min?: number;
  max?: number;
  max_per_answer?: number;
}

export interface OrderableMenuAnswer {
  id: string;
  text: string;
  parent: string;
  sort: number;
  available?: boolean;
  price?: number;
  prices?: OrderableMenuPrice[];
}

export interface OrderableMenuItem {
  id: string;
  name: string;
  description?: string;
  category: number;
  meta_category?: number;
  service?: string[];
  sort?: number;
  preparation?: number;
  type?: string;
  tax?: number;
  prices: OrderableMenuPrice[];
  media?: any[];
  favorite?: boolean;
  questions: OrderableMenuQuestion[];
  answers: OrderableMenuAnswer[];
}

export interface OrderableMenuCategory {
  id: number;
  name: string;
  description?: string;
  sort?: number;
  media?: any[];
}

export interface OrderableMenuDelays {
  window: number;
  delay: number;
  route: number;
}

export interface OrderableMenuResponse {
  restaurantId: string;
  restaurantName: string;
  mode: "takeout" | "delivery";
  timing: "asap" | "scheduled";
  scheduledStart?: number;
  scheduledEnd?: number;
  categories: OrderableMenuCategory[];
  items: OrderableMenuItem[];
  delays?: OrderableMenuDelays;
  locationValid?: boolean;
  locationError?: string;
}

export interface OrderConfig {
  serviceType: "takeout" | "delivery";
  timing: "asap" | "scheduled";
  scheduledStart?: number;
  scheduledEnd?: number;
  addressId?: number;
  // Store address coordinates for map display on checkout (fallback when address lookup fails)
  addressLatitude?: number;
  addressLongitude?: number;
}

export function useOrderableMenu(
  restaurantId: string | undefined,
  orderConfig: OrderConfig | null
) {
  const isEnabled = !!restaurantId && !!orderConfig;

  return useQuery<OrderableMenuResponse>({
    queryKey: [
      "/api/restaurants",
      restaurantId,
      "menu",
      orderConfig?.timing || "asap",
      orderConfig?.serviceType,
      orderConfig?.scheduledStart,
      orderConfig?.scheduledEnd,
    ],
    queryFn: async () => {
      if (!restaurantId || !orderConfig) {
        throw new Error("Restaurant ID and order config required");
      }

      let url: string;
      if (orderConfig.timing === "scheduled" && orderConfig.scheduledStart && orderConfig.scheduledEnd) {
        const params = new URLSearchParams({
          mode: orderConfig.serviceType,
          start: orderConfig.scheduledStart.toString(),
          end: orderConfig.scheduledEnd.toString(),
        });
        url = `/api/restaurants/${restaurantId}/menu/target?${params.toString()}`;
      } else {
        url = `/api/restaurants/${restaurantId}/menu/asap?mode=${orderConfig.serviceType}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to fetch menu");
      }
      return response.json();
    },
    enabled: isEnabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useDeliveryCoverage(
  restaurantId: string | undefined,
  latitude: number | null,
  longitude: number | null
) {
  const isEnabled = !!restaurantId && latitude !== null && longitude !== null;

  return useQuery<{ restaurantId: string; covered: boolean; message?: string }>({
    queryKey: ["/api/restaurants", restaurantId, "coverage", latitude, longitude],
    queryFn: async () => {
      if (!restaurantId || latitude === null || longitude === null) {
        throw new Error("Restaurant ID and coordinates required");
      }

      const params = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
      });
      const response = await fetch(`/api/restaurants/${restaurantId}/coverage?${params.toString()}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to check coverage");
      }
      return response.json();
    },
    enabled: isEnabled,
    staleTime: 60000,
  });
}

export function useOrderDelays(restaurantId: string | undefined) {
  return useQuery<{
    restaurantId: string;
    takeoutMinutes: number;
    deliveryMinutes: number;
    takeoutSeconds: number;
    deliverySeconds: number;
  }>({
    queryKey: ["/api/restaurants", restaurantId, "delays"],
    queryFn: async () => {
      if (!restaurantId) {
        throw new Error("Restaurant ID required");
      }
      const response = await fetch(`/api/restaurants/${restaurantId}/delays`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to fetch delays");
      }
      return response.json();
    },
    enabled: !!restaurantId,
    staleTime: 60000,
  });
}

export interface AsapAvailabilityResponse {
  available: boolean;
  hasItems: boolean;
  delays?: OrderableMenuDelays;
  itemCount?: number;
}

export function useAsapAvailability(
  restaurantId: string | undefined,
  serviceType: 'takeout' | 'delivery',
  enabled: boolean = true
) {
  return useQuery<AsapAvailabilityResponse>({
    queryKey: ["/api/restaurants", restaurantId, "menu/asap", "availability", serviceType],
    queryFn: async () => {
      if (!restaurantId) {
        throw new Error("Restaurant ID required");
      }
      
      const url = `/api/restaurants/${restaurantId}/menu/asap?mode=${serviceType}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        return {
          available: false,
          hasItems: false,
        };
      }
      
      const data = await response.json();
      const hasItems = data.items && data.items.length > 0;
      
      return {
        available: hasItems,
        hasItems,
        delays: data.delays,
        itemCount: data.items?.length || 0,
      };
    },
    enabled: !!restaurantId && enabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}
